import { ChangeEvent, useCallback, useState } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import Loading from '@/components/general/Loading';
import { PageLink } from '@/components/TopPageLinks/TopPageLinks.component';
import useHasRoutePermission from '@/hooks/useHasRoutePermission';
import useProfile from '@/hooks/useProfileV2';
import { chakraExtendedTheme } from '@/styles/theme';
import { Paths } from '@/types/routes';
import HomeComponent from './Home.component';
import * as Styles from './Home.styles';

const HomeContainer = (): JSX.Element => {
  const { profile, isAdmin } = useProfile();
  const [search, setSearch] = useState<string>('');
  const location = useLocation();

  const handleSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      setSearch(event.target.value);
    },
    [setSearch]
  );

  const hasDashboardPermission = useHasRoutePermission(Paths.HOME);
  const hasPersonalInfoPermission = useHasRoutePermission(Paths.HOME_PERSONAL_INFO);
  const hasTimeOffPermission = useHasRoutePermission(Paths.HOME_PTO_REQUESTS);
  const hasAttachmentsPermission = useHasRoutePermission(Paths.HOME_ATTACHMENTS);

  const { isLoading = false } = profile;

  const pageLinks: PageLink[] = [
    {
      label: 'Dashboard',
      to: {
        path: Paths.HOME,
      },
      isActived: location.pathname === Paths.HOME,
      isDisabled: !hasDashboardPermission,
    },
    {
      label: 'Personal Info',
      to: {
        path: Paths.HOME_PERSONAL_INFO,
      },
      isActived: location.pathname === Paths.HOME_PERSONAL_INFO,
      isDisabled: !hasPersonalInfoPermission,
    },
    // DEV: Comment until the PTO feature is released
    // {
    //   label: 'PTO',
    //   to: {
    //     path: Paths.HOME_PTO_REQUESTS,
    //   },
    //   isActived: location.pathname === Paths.HOME_PTO_REQUESTS,
    //   isDisabled: !hasTimeOffPermission,
    // },
  ];

  if (isAdmin) {
    pageLinks.push({
      label: 'Attachments',
      to: {
        path: Paths.HOME_ATTACHMENTS,
      },
      isActived: location.pathname === Paths.HOME_ATTACHMENTS,
      isDisabled: !hasAttachmentsPermission,
    });
  }

  if (isLoading) {
    return (
      <Styles.Box>
        <Loading fullPage />
      </Styles.Box>
    );
  }

  return (
    <ChakraProvider theme={chakraExtendedTheme}>
      <HomeComponent searchValue={search} onSearch={handleSearch} pageLinks={pageLinks} />
    </ChakraProvider>
  );
};

export default HomeContainer;
